import { Theme, createTheme } from '@mui/material/styles';
import React, { MouseEvent } from 'react';
import { SelectChangeEvent } from '@mui/material/Select';

import { HOSTNAME_IS_TEST, IS_DEV, IS_PROD, IS_STAGING } from '../constants/environment';
import themeImporter from '../themes/themeImporter';
import { requestHandler } from '../utils';
import { getDevHost } from '../utils/tokenManager';
import UserContext from './userContext';
import defaultTheme from '../themes/defaultTheme';

interface DomainData {
    id: number;
    clientId: number;
    client: {
        id: number;
        accountName: string;
        salesforceId: string | null;
        hubName: string | null;
        storeUrl: string | null;
    };
    domain: string | null;
    isLive: boolean;
    domainUISetting?: {
        communityChatDisabled: boolean | null;
        groupChatDisabled: boolean | null;
        campaignChatDisabled: boolean | null;
        directMessagingDisabled: boolean | null;
        clientCommunityChatDisabled: boolean | null;
        clientGroupChatDisabled: boolean | null;
        paymentsDisabled: boolean | null;
        productsDisabled: boolean | null;
        streamingEnabled: boolean | null;
        postsDisabled: boolean | null;
        messagingDisabled: boolean | null;
        dashboardDisabled: boolean | null;
        reportingDisabled: boolean | null;
        campaignsDisabled: boolean | null;
        logoUrl: string | null;
        backgroundColor: string | null;
        headerColor: string | null;
        logoWidth: number | null;
        title: string | null;
        faviconUrl: string | null;
        defaultRoute: string | null;
        hideDiscounts: boolean | null;
        hideReferralLink: boolean | null;
        primaryColor: string | undefined;
    };
    domainSetting?: {
        id: number;
        clientHubId: number;
        locationType: string | null;
        askPhone: string | null;
        askLocation: string | null;
        applicationHeader: string | null;
        customQuestionHeader: string | null;
        askAge: string | null;
        askGender: string | null;
    };
    hasLandingPage: boolean;
    sourceTag: string | null;
}

export interface DomainOption {
    id: number;
    client: {
        id: number;
        accountName: string;
    };
    clientId: number;
    domain: string;
    hasLandingPage: boolean | null;
    isLive: boolean;
    sourceTag: string | null;
}

interface DomainContextValue {
    domainData?: DomainData;
    domainOptions?: DomainOption[];
    selectedDomain?: string;
    theme?: Theme;
    serverError?: string;
    handleDomainSelectChange: (e: SelectChangeEvent<string>) => void;
    handleDomainSelectClose: (e: MouseEvent<HTMLElement>) => void;
    handleSetDomain: () => void;
}

const DomainContext = React.createContext<DomainContextValue | undefined>(undefined);

export const DomainContextProvider = ({ children }: { children: React.ReactNode }) => {
    const [domainData, setDomainData] = React.useState<DomainData>();
    const [selectedDomain, setSelectedDomain] = React.useState('');
    const [domainOptions, setDomainOptions] = React.useState<DomainOption[]>();
    const [theme, setTheme] = React.useState<Theme>();
    const [serverError, setServerError] = React.useState<string | undefined>();
    const { activeClient, fetchedUser, id: userId } = React.useContext(UserContext);

    const getClientByDomain = React.useCallback(
        async (urlBase = '/api/public') => {
            if (
                IS_PROD ||
                (IS_STAGING && window.location.host.indexOf('.test.') === -1) ||
                ((IS_DEV || (IS_STAGING && !HOSTNAME_IS_TEST)) && selectedDomain && !domainOptions)
            ) {
                const res = await requestHandler<DomainData>(
                    'GET',
                    `${urlBase}/domainData?domain=${
                        IS_PROD || (IS_STAGING && !HOSTNAME_IS_TEST)
                            ? window.location.host
                            : getDevHost()
                    }`,
                    undefined,
                );
                if (!res.success && res.status === 500) {
                    setServerError('db connection error');
                } else {
                    setDomainData(res.payload);
                }
            }
        },
        [selectedDomain, domainOptions],
    );

    const handleDomainSelectChange = React.useCallback((e: SelectChangeEvent<string>) => {
        setSelectedDomain(e.target.value);
    }, []);

    const handleDomainSelectClose = React.useCallback(() => setDomainOptions(undefined), []);

    const loadTheme = React.useCallback(async () => {
        if (domainData && fetchedUser) {
            try {
                let { id: clientId } = domainData.client;
                if (activeClient?.userClients?.userType === 'user') clientId = 1;

                const newTheme = await themeImporter(domainData?.domainUISetting, clientId);
                setTheme(createTheme(newTheme));
            } catch (error) {
                console.error('Error loading theme:', error);
                setTheme(createTheme(defaultTheme));
            }
        }
    }, [domainData, fetchedUser, activeClient?.userClients?.userType]);

    React.useEffect(() => {
        loadTheme();
    }, [loadTheme]);

    React.useEffect(() => {
        if (activeClient?.userClients?.userType === 'user' && domainData?.client?.id !== 1) {
            getClientByDomain('/api');
        }
    }, [activeClient?.userClients?.userType, domainData?.client?.id, getClientByDomain]);

    const handleSetDomain = () => {
        localStorage.setItem('devHost', selectedDomain);
        setDomainOptions(undefined);
    };

    React.useEffect(() => {
        const fetchData = async () => {
            if (
                fetchedUser &&
                (!userId || !activeClient) &&
                (IS_DEV || (IS_STAGING && !HOSTNAME_IS_TEST))
            ) {
                if (IS_DEV && !getDevHost(true)) {
                    const res = await requestHandler('GET', '/api/public/domains', undefined);
                    if (res.success) setDomainOptions(res.payload as DomainOption[]);
                } else setSelectedDomain(getDevHost() || '');
            } else if (fetchedUser) {
                setSelectedDomain(getDevHost() || '');
            }
        };
        fetchData();
    }, [fetchedUser, userId, activeClient]);

    React.useEffect(() => {
        getClientByDomain();
    }, [getClientByDomain]);

    return (
        <DomainContext.Provider
            value={{
                domainData,
                domainOptions,
                selectedDomain,
                theme,
                serverError,
                handleDomainSelectChange,
                handleDomainSelectClose,
                handleSetDomain,
            }}
        >
            {children}
        </DomainContext.Provider>
    );
};

export default DomainContext;
